<template>
  <PlatformLayout title="Сообщения" :show-guest-banner="false">
    <div class="msg-page">
      <!-- Пока не знаем состояния — скелет, а не пустота: пустой экран
           читается как «вам никто не писал». -->
      <div v-if="loading" class="msg-page__skeleton" aria-busy="true">
        <div v-for="n in 5" :key="n" class="msg-sk" :class="{ 'msg-sk--mine': n % 2 === 0 }" />
      </div>

      <!-- Экран отказа — ТОЛЬКО когда открывать нечего. Отказ, случившийся на
           открытом чате (истекла сессия), показывается плашкой внутри: подмени
           здесь весь экран — и вместе с ним исчезнет композитор с набранным
           текстом и уже загруженными вложениями. -->
      <div v-else-if="fatal && !chat.chat_uuid" class="msg-page__fatal">
        <h2 class="msg-page__fatal-title">Переписка не открылась</h2>
        <p class="msg-page__fatal-text">{{ fatal }}</p>
        <button type="button" class="msg-page__retry" @click="open">Попробовать снова</button>
      </div>

      <section v-else class="msg-chat">
        <header class="msg-chat__head">
          <div class="msg-chat__avatar" aria-hidden="true">{{ initials }}</div>
          <div class="msg-chat__who">
            <h2 class="msg-chat__title">{{ heading }}</h2>
            <p class="msg-chat__sub">{{ subtitle }}</p>
          </div>
          <!-- Обрыв связи виден постоянно, а не всплывашкой на три секунды:
               человек должен понимать, почему ничего не приходит. -->
          <span v-if="offline" class="msg-chat__offline">нет связи</span>
        </header>

        <p v-if="fatal" class="msg-chat__fatal" role="alert">{{ fatal }}</p>

        <!-- `aria-live` НА ЛЕНТЕ НЕ ВИСИТ. Догрузка истории — это тоже
             добавление узлов, и экранный диктор зачитывал вслух полсотни
             старых сообщений подряд, стоило долистать вверх. Вслух говорит
             отдельная область ниже, и только про пришедшее сейчас. -->
        <div
          ref="feed"
          class="msg-chat__feed"
          role="log"
          aria-label="Лента сообщений"
          @scroll="onScroll"
        >
          <div v-if="loadingMore" class="msg-chat__more">Загружаем историю…</div>
          <button
            v-else-if="hasMore"
            type="button"
            class="msg-chat__more msg-chat__more--button"
            @click="loadOlder"
          >
            Показать историю раньше
          </button>

          <!-- Лента не загрузилась — так и говорим. «Здесь появится
               переписка» на месте недоехавшей истории читается как «вам никто
               не писал»: человек уходит, решив, что куратор молчит. -->
          <div v-if="!messages.length && loadFailed" class="msg-empty">
            <h3 class="msg-empty__title">Переписка не загрузилась</h3>
            <p class="msg-empty__text">Проверьте связь — сообщения на месте, их просто не удалось получить.</p>
            <button type="button" class="msg-page__retry" @click="loadLatest">Попробовать снова</button>
          </div>

          <div v-else-if="!messages.length" class="msg-empty">
            <div class="msg-empty__icon" aria-hidden="true">
              <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
              </svg>
            </div>
            <h3 class="msg-empty__title">Здесь появится переписка</h3>
            <p class="msg-empty__text">
              Напишите куратору — он ответит в этом чате. Уведомление придёт и в приложение.
            </p>
          </div>

          <template v-for="item in feedItems" :key="item.key">
            <div v-if="item.day" class="msg-day"><span>{{ item.day }}</span></div>
            <MessageBubble
              v-else
              :message="item.message"
              :replyable="chat.can_write !== false"
              @reply="startReply"
            />
          </template>
        </div>

        <!-- Что зачитать вслух: только что пришедшее, одной строкой. -->
        <p class="msg-chat__sr" aria-live="polite">{{ announce }}</p>

        <MessageComposer
          ref="composer"
          v-if="chat.can_write !== false"
          :chat-uuid="chat.chat_uuid"
          :reply-to="replyTo"
          @sent="onSent"
          @cancel-reply="replyTo = null"
        />
        <div v-else class="msg-chat__closed">Переписка закрыта для новых сообщений.</div>
      </section>
    </div>
  </PlatformLayout>
</template>

<script>
import PlatformLayout from '@/components/platform/PlatformLayout.vue';
import MessageBubble from '@/components/messenger/MessageBubble.vue';
import MessageComposer from '@/components/messenger/MessageComposer.vue';
import messenger from '@/utils/messenger';

/** Темп опроса открытого чата. Реже — заметна задержка, чаще — впустую. */
const POLL_MS = 4000;

/** Насколько близко к низу считаем «человек внизу». */
const STICK_PX = 60;

/**
 * Через сколько перечитывать ленту ради свежих ссылок на файлы.
 *
 * Подпись живёт 15 минут — берём с запасом, чтобы человек не успел ткнуть в
 * мёртвую ссылку.
 */
const LINKS_TTL_MS = 10 * 60 * 1000;

export default {
  name: 'MessagesView',
  components: { PlatformLayout, MessageBubble, MessageComposer },
  data() {
    return {
      chat: {},
      messages: [],
      loading: true,
      loadingMore: false,
      fatal: '',
      offline: false,
      hasMore: false,
      cursorAfter: 0,
      cursorBefore: null,
      /** Держимся низа, только если человек и был внизу. */
      stick: true,
      timer: null,
      /** Когда лента загружалась целиком — от этого считается свежесть ссылок. */
      loadedAt: 0,
      /** Строка для экранного диктора: только что пришедшее. */
      announce: '',
      /**
       * На какое сообщение отвечаем — ЦЕЛИКОМ, а не одним номером.
       *
       * Живёт ЗДЕСЬ, а не в композиторе: цель выставляет пузырь, а показывает
       * композитор, и общий владелец у них один — этот экран. Разговор
       * говорит только пропсами вниз и событиями вверх, своей шины сообщений
       * в кабинете для этого нет.
       */
      replyTo: null,
      growth: null,
      onMediaLoad: null,
      /*
       * Жив ли ещё экран.
       *
       * ЗАПРОС НЕ ОТМЕНЯЕТСЯ, А КОМПОНЕНТ УХОДИТ. Человек открывает
       * «Сообщения» и, пока идут два первых запроса, уходит в другой раздел:
       * beforeUnmount отрабатывает на пустом месте — таймера ещё нет, снимать
       * нечего, — а через долю секунды open() дорезолвивается у мёртвого
       * экземпляра и заводит опрос, который уже НИКТО не остановит. Дальше до
       * конца жизни вкладки мёртвая страница каждые 4 секунды отмечает
       * сообщения куратора прочитанными за спиной человека и гасит значок
       * непрочитанного во всём кабинете.
       */
      alive: true,
    };
  },
  computed: {
    initials() {
      const source = this.heading;
      return source
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0])
        .join('')
        .toUpperCase();
    },
    /**
     * Лента так и не доехала.
     *
     * Именно «не доехала», а не «нет связи»: связь могла оборваться и на
     * сотом опросе — тогда сообщения на экране есть, и говорить о провале
     * загрузки не о чем.
     */
    loadFailed() {
      return this.offline && !this.loadedAt;
    },
    /** Сотрудники разговора — те, кто не клиент. */
    staff() {
      return (this.chat.participants_preview || [])
        .filter((p) => !String(p.ref || '').startsWith('platform:'));
    },
    /**
     * Заголовок чата.
     *
     * СЕРВЕРНЫЙ `title` СЮДА НЕ ГОДИТСЯ: у чата клиента там снимок ФИО САМОГО
     * клиента — он собран для списка чатов у сотрудника. Покажи его в кабинете,
     * и человек увидит крупным шрифтом собственное имя, как будто переписывается
     * сам с собой.
     */
    heading() {
      if (this.staff.length === 1) return this.staff[0].name;
      if (this.staff.length > 1) return 'Ваша команда';

      return 'MentorMe';
    },
    subtitle() {
      if (!this.staff.length) return 'Куратор подключится, когда его назначат';

      if (this.staff.length === 1) {
        const role = this.staff[0].role_label;
        return role ? role.charAt(0).toUpperCase() + role.slice(1).toLowerCase() : 'Ваш куратор';
      }

      return this.staff.map((p) => p.name).join(', ');
    },
    /**
     * Лента с разделителями дней.
     *
     * Разделитель считаем САМИ, а не по `day_first` сервера: тот считается
     * внутри страницы, и первое сообщение любой догруженной страницы всегда
     * помечено первым в дне — на стыке страниц получилось бы два разделителя
     * одного дня подряд.
     */
    feedItems() {
      const out = [];
      let previous = '';

      for (const message of this.messages) {
        const day = this.dayOf(message.created_at);

        if (day && day !== previous) {
          out.push({ key: `day-${day}`, day: this.dayLabel(message.created_at) });
          previous = day;
        }

        out.push({ key: message.message_uuid, message });
      }

      return out;
    },
  },
  methods: {
    /**
     * Ключ дня — по МЕСТНОМУ времени, а не срезом строки.
     *
     * Срез брал дату в часовом поясе СЕРВЕРА: у человека в другом поясе
     * сообщение, отправленное в час ночи, попадало в группу «вчера», а подпись
     * под ней считалась по его часам — получалось «Сегодня» над вчерашним.
     */
    dayOf(iso) {
      if (!iso) return '';

      const d = new Date(iso);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    },
    dayLabel(iso) {
      const date = new Date(iso);
      const today = new Date();
      const yesterday = new Date(Date.now() - 86400000);

      const same = (a, b) => a.toDateString() === b.toDateString();
      if (same(date, today)) return 'Сегодня';
      if (same(date, yesterday)) return 'Вчера';

      return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
    },

    /** Открыть чат: он заведётся сам, если человеку ещё не писали. */
    async open() {
      this.loading = true;
      this.fatal = '';

      const res = await messenger.myChat();
      if (!this.alive) return;

      if (!res.ok || !res.data) {
        this.fatal = res.status === 401
          ? 'Сессия истекла — войдите заново.'
          : res.error || 'Попробуйте обновить страницу.';
        this.loading = false;
        return;
      }

      this.chat = res.data;
      await this.loadLatest();
      if (!this.alive) return;

      this.loading = false;

      this.$nextTick(() => {
        this.toBottom(true);
        this.watchGrowth();
      });
      this.startPolling();
    },

    /**
     * Перечитать ленту целиком.
     *
     * ССЫЛКИ НА ФАЙЛЫ ЖИВУТ 15 МИНУТ. Опрос приносит только новые сообщения, а
     * старые остаются с подписью, выданной при первой загрузке: вкладку держат
     * открытой часами, и через двадцать минут ЛЮБОЙ файл в ленте перестаёт
     * открываться — с отказом, который человек прочитает как «файл пропал».
     * Поэтому раз в десять минут забираем ленту заново со свежими подписями.
     */
    async refreshLinks() {
      if (Date.now() - this.loadedAt < LINKS_TTL_MS) return;
      if (!this.chat.chat_uuid || document.visibilityState !== 'visible') return;

      await this.loadLatest({ keepHistory: false });

      // И заодно ШАПКУ. Куратора назначают уже после того, как человек открыл
      // вкладку: «Куратор скоро подключится» иначе висит над перепиской с
      // куратором до перезагрузки страницы.
      const res = await messenger.myChat();
      if (this.alive && res.ok && res.data) this.chat = res.data;
    },

    async loadLatest() {
      const res = await messenger.messages(this.chat.chat_uuid, { limit: 50 });
      if (!this.alive) return;

      if (!res.ok) {
        this.offline = true;
        return;
      }

      this.offline = false;
      this.messages = res.data.messages || [];
      this.loadedAt = Date.now();
      this.cursorAfter = res.data.next_after_seq ?? res.data.last_seq ?? 0;
      this.cursorBefore = res.data.next_before_seq ?? null;
      this.hasMore = !!res.data.has_more;

      this.markRead(res.data.last_seq);
    },

    /** История вверх. Прокрутку удерживаем на месте, иначе экран прыгает. */
    async loadOlder() {
      if (this.loadingMore || !this.cursorBefore) return;

      this.loadingMore = true;
      const box = this.$refs.feed;
      const before = box ? box.scrollHeight : 0;

      const res = await messenger.messages(this.chat.chat_uuid, {
        beforeSeq: this.cursorBefore,
        limit: 50,
      });
      if (!this.alive) return;

      this.loadingMore = false;

      if (!res.ok) {
        this.offline = true;
        return;
      }

      const older = res.data.messages || [];
      const known = new Set(this.messages.map((m) => m.message_uuid));

      this.messages = [...older.filter((m) => !known.has(m.message_uuid)), ...this.messages];
      this.cursorBefore = res.data.next_before_seq ?? null;
      this.hasMore = !!res.data.has_more;

      this.$nextTick(() => {
        if (box) box.scrollTop = box.scrollHeight - before;
      });
    },

    /**
     * Опрос: что нового после курсора.
     *
     * `has_more` здесь значит «ты отстал» — зовём сразу ещё раз, не дожидаясь
     * следующего тика: так подтягивается пропущенное после сна устройства.
     */
    async poll(depth = 0) {
      if (!this.alive || !this.chat.chat_uuid || document.visibilityState !== 'visible') return;

      const res = await messenger.messages(this.chat.chat_uuid, {
        afterSeq: this.cursorAfter,
        limit: 50,
      });
      if (!this.alive) return;

      if (!res.ok) {
        // Протух токен — опрашивать бессмысленно: каждый следующий запрос
        // получит тот же отказ, а человек будет смотреть на «нет связи» и
        // ждать. Говорим прямо и останавливаемся.
        if (res.status === 401) {
          this.stopPolling();
          this.fatal = 'Сессия истекла — войдите заново.';
          return;
        }

        this.offline = true;
        return;
      }

      this.offline = false;

      const fresh = res.data.messages || [];

      if (fresh.length) {
        this.append(fresh);
        this.speak(fresh);
        this.$nextTick(() => this.toBottom());
      }

      this.cursorAfter = res.data.next_after_seq ?? this.cursorAfter;

      // Рубежи чужого прочтения приезжают тем же ответом: галочка на СТАРОМ
      // сообщении иначе не изменилась бы никогда.
      this.applyBorders(res.data.read_borders || []);

      // ПРОЧИТАНО — ТОЛЬКО ТО, ЧТО ЧЕЛОВЕК ВИДЕЛ. Он может читать историю
      // вверху ленты; пришедшее в этот момент вниз он не видел, и ставить по
      // нему рубеж значит соврать собеседнику галочкой. Вернётся вниз —
      // `onScroll` поднимет `stick`, и следующий тик отметит.
      if (this.stick && (fresh.length || res.data.unread)) this.markRead(res.data.last_seq);

      // Отставание догоняем в том же тике, но не бесконечно: три страницы —
      // полторы сотни сообщений, дальше подождёт следующего тика.
      if (res.data.has_more && depth < 3) await this.poll(depth + 1);

      // И заодно смотрим, не пора ли обновить ссылки на файлы.
      if (depth === 0) this.refreshLinks();
    },

    /**
     * Пересчитать галочки по рубежам.
     *
     * Сервер присылает рубеж каждого участника; «прочитано» — это
     * `seq <= рубеж`. Ждать, пока сообщение приедет заново, бессмысленно: оно
     * не изменится, изменится только чужое прочтение.
     */
    applyBorders(borders) {
      if (!borders.length) return;

      const top = Math.max(...borders.map((b) => b.last_read_seq || 0));

      for (const message of this.messages) {
        if (!message.meta?.is_mine) continue;

        const read = message.meta.read || (message.meta.read = {});
        read.by_count = message.seq <= top ? Math.max(1, read.by_count || 0) : 0;
      }
    },

    /**
     * Сказать вслух про пришедшее.
     *
     * Одно сообщение читаем целиком, несколько — считаем: диктор, зачитывающий
     * пять реплик подряд, перекрывает сам себя и не даёт услышать ни одну.
     */
    speak(fresh) {
      const mine = (m) => m.meta?.is_mine;
      const theirs = fresh.filter((m) => !mine(m));

      if (!theirs.length) return;

      const one = theirs[theirs.length - 1];
      const who = one.meta?.actor?.name || 'Собеседник';

      this.announce = theirs.length === 1
        ? `${who}: ${one.text || 'вложение'}`
        : `Новых сообщений: ${theirs.length}`;
    },

    markRead(upToSeq) {
      if (!upToSeq || !this.alive) return;

      messenger.markRead(this.chat.chat_uuid, upToSeq);
      this.$store.dispatch('messenger/clear');
    },

    /**
     * Дописать в конец, не задваивая.
     *
     * ГОНКА РЕАЛЬНАЯ. Опрос уходит на сервер, человек в этот момент
     * отправляет сообщение — и в ответе на опрос приезжает то же самое, что мы
     * уже показали ответом на отправку. Без сверки в ленте окажется два
     * одинаковых пузыря с ОДНИМ ключом, а Vue на такое отвечает поломкой
     * отрисовки, а не понятной ошибкой.
     */
    append(incoming) {
      const known = new Set(this.messages.map((m) => m.message_uuid));

      for (const message of incoming) {
        if (!known.has(message.message_uuid)) {
          this.messages.push(message);
          known.add(message.message_uuid);
        }
      }
    },

    /** Начать ответ: цель запомнили, курсор — в поле, чтобы можно было писать. */
    startReply(message) {
      this.replyTo = message;

      // `?.` обязателен: композитора нет в чате, закрытом для записи. Кнопку
      // мы там и не рисуем, но полагаться на одну лишь разметку в обработчике,
      // который она же и вызывает, — это ждать падения при первой правке.
      this.$nextTick(() => this.$refs.composer?.focusInput());
    },

    onSent(message) {
      if (!message) return;

      /*
       * СВОЮ ЦИТАТУ ДОРИСОВЫВАЕМ САМИ, и это не украшательство.
       *
       * Ответ на отправку приходит усечённым: `reply_to_preview` в нём ВСЕГДА
       * пусто, даже когда цитата есть. Полный конверт приедет ближайшим
       * опросом — но `append()` пропускает уже известный номер и ничего не
       * заменяет, то есть отбросит его. Без этой сборки свой ответ висел бы
       * без цитаты до перезагрузки страницы.
       *
       * Сто сорок символов — ровно столько отдаёт сервер, чтобы своя цитата
       * выглядела так же, как после перезагрузки.
       */
      const answered = this.replyTo
        && message.meta?.reply_to_message_uuid === this.replyTo.message_uuid;

      if (answered) {
        message.meta.reply_to_preview = {
          actor_name: this.replyTo.meta?.actor?.name || null,
          // По СИМВОЛАМ, а не по кодовым единицам: `slice` рубит суррогатную
          // пару пополам, и на месте эмодзи встаёт «крокозябра». Сервер режет
          // mb_substr — здесь то же самое.
          text: [...(this.replyTo.data?.text || '')].slice(0, 140).join('') || null,
          render_hint: this.replyTo.type,
        };
      }

      // Гасим ТОЛЬКО ту цитату, которая уехала с этим сообщением. Пока шла
      // отправка, человек мог нажать «Ответить» на другое — безусловное
      // гашение стирало бы этот выбор молча. И наоборот: не погаси её вовсе —
      // каждое следующее сообщение уедет ответом на то же старое, а полоска
      // над полем будет выглядеть нормально.
      if (answered) {
        this.replyTo = null;
      }

      // Своё сообщение показываем сразу: ждать тика опроса — значит смотреть
      // на пустое поле и гадать, ушло ли.
      this.append([message]);

      // КУРСОР НЕ ТРОГАЕМ. Своё сообщение получает номер БОЛЬШЕ, чем у чужих,
      // которые опрос ещё не успел забрать: подвинь курсор к нему — и всё, что
      // собеседник написал между тиками, не приедет НИКОГДА. Пусть ближайший
      // опрос принесёт и чужое, и наше: наше он отсеет по номеру сообщения.
      this.stick = true;
      this.$nextTick(() => this.toBottom(true));

      // И спрашиваем сразу: ответ собеседника придёт быстрее следующего тика.
      this.poll();
    },

    onScroll() {
      const box = this.$refs.feed;
      if (!box) return;

      this.stick = box.scrollHeight - box.scrollTop - box.clientHeight < STICK_PX;

      // Долистали до верха — подтягиваем историю сами, без кнопки.
      if (box.scrollTop < 40 && this.hasMore && !this.loadingMore) this.loadOlder();
    },

    /**
     * Доехать вниз, но только если человек и был внизу.
     *
     * Рывок на каждом новом сообщении у того, кто читает историю выше, хуже
     * пропущенного сообщения.
     */
    toBottom(force = false) {
      const box = this.$refs.feed;
      if (!box || (!this.stick && !force)) return;

      box.scrollTop = box.scrollHeight;
    },

    /**
     * Держать низ, когда лента выросла НЕ от нового сообщения.
     *
     * Два случая, и оба выглядят поломкой. Картинки грузятся после разметки:
     * прокрутка вниз уже случилась, а высота ленты потом выросла — человек
     * при первом открытии оказывался в середине переписки. И композитор
     * растёт под длинный текст, отъедая низ ленты: последние строки уезжают
     * под поле ввода, и никто их не возвращает.
     */
    watchGrowth() {
      const box = this.$refs.feed;
      if (!box) return;

      // `load` не всплывает — слушаем на этапе погружения.
      this.onMediaLoad = () => this.toBottom();
      box.addEventListener('load', this.onMediaLoad, true);

      if (!window.ResizeObserver) return;

      this.growth = new ResizeObserver(() => this.toBottom());
      this.growth.observe(box);
    },

    unwatchGrowth() {
      const box = this.$refs.feed;

      if (box && this.onMediaLoad) box.removeEventListener('load', this.onMediaLoad, true);
      if (this.growth) this.growth.disconnect();

      this.growth = null;
      this.onMediaLoad = null;
    },

    startPolling() {
      if (this.timer) return;

      this.timer = setInterval(() => this.poll(), POLL_MS);
      document.addEventListener('visibilitychange', this.onVisible);
    },

    stopPolling() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
      document.removeEventListener('visibilitychange', this.onVisible);
    },

    onVisible() {
      // Вернулись на вкладку — спрашиваем сразу: ждать тик значит смотреть на
      // устаревшую ленту.
      if (document.visibilityState === 'visible') this.poll();
    },
  },

  mounted() {
    this.open();
  },

  beforeUnmount() {
    // ПОРЯДОК ВАЖЕН: сначала флаг, потом снятие. Флаг — единственное, что
    // остановит ответы, которые вернутся уже после ухода со страницы.
    this.alive = false;
    this.stopPolling();
    this.unwatchGrowth();
  },
};
</script>

<style lang="scss" scoped>
.msg-page {
  height: 100%;
  min-height: 0;
  font-family: 'Manrope', sans-serif;

  &__skeleton {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 20px;
  }

  &__fatal {
    max-width: 420px;
    margin: 60px auto;
    text-align: center;
  }

  &__fatal-title {
    margin: 0 0 8px;
    font-size: 18px;
    font-weight: 700;
    color: #111827;
  }

  &__fatal-text {
    margin: 0 0 16px;
    font-size: 14px;
    color: #6b7280;
  }

  &__retry {
    padding: 9px 18px;
    border: none;
    border-radius: 10px;
    background: #11415d;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }
}

.msg-sk {
  width: 55%;
  height: 46px;
  border-radius: 14px;
  background: linear-gradient(90deg, #f3f5f7 25%, #e8eaed 37%, #f3f5f7 63%);
  background-size: 400% 100%;
  animation: msg-shimmer 1.3s ease infinite;

  &--mine {
    align-self: flex-end;
  }
}

.msg-chat {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 64px - 48px);
  min-height: 420px;
  border: 1px solid #e8eaed;
  border-radius: 16px;
  background: #fff;
  overflow: hidden;

  &__head {
    flex: none;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 18px;
    border-bottom: 1px solid #e8eaed;
  }

  &__avatar {
    flex: none;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #11415d;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
  }

  &__who {
    flex: 1;
    min-width: 0;
  }

  &__title {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: #111827;
  }

  &__sub {
    margin: 2px 0 0;
    font-size: 12.5px;
    color: #6b7280;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__offline {
    flex: none;
    padding: 3px 10px;
    border-radius: 999px;
    background: #fdeaec;
    color: #e8192c;
    font-size: 11.5px;
    font-weight: 600;
  }

  // Плашка отказа на открытом чате: композитор под ней остаётся жив.
  &__fatal {
    margin: 0;
    padding: 9px 18px;
    border-bottom: 1px solid #fbd5d9;
    background: #fdeaec;
    color: #e8192c;
    font-size: 13px;
    font-weight: 600;
  }

  // Область для экранного диктора: слышно, но не видно.
  &__sr {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }

  &__feed {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 16px 18px;
    background: #fff;
  }

  &__more {
    margin: 0 auto 12px;
    display: block;
    padding: 6px 14px;
    border: none;
    border-radius: 999px;
    background: #f3f5f7;
    color: #6b7280;
    font-size: 12.5px;
    text-align: center;

    &--button {
      cursor: pointer;

      &:hover {
        background: #e8eaed;
        color: #111827;
      }
    }
  }

  &__closed {
    flex: none;
    padding: 14px 18px;
    border-top: 1px solid #e8eaed;
    font-size: 13px;
    color: #6b7280;
    text-align: center;
  }
}

.msg-day {
  display: flex;
  justify-content: center;
  margin: 14px 0 10px;

  span {
    padding: 3px 12px;
    border-radius: 999px;
    background: #f3f5f7;
    font-size: 11.5px;
    font-weight: 600;
    color: #6b7280;
  }
}

.msg-empty {
  max-width: 340px;
  margin: 48px auto;
  text-align: center;

  &__icon {
    color: #d1d5db;
    margin-bottom: 10px;
  }

  &__title {
    margin: 0 0 6px;
    font-size: 15.5px;
    font-weight: 700;
    color: #111827;
  }

  &__text {
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: #6b7280;
  }
}

@keyframes msg-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

@media (max-width: 768px) {
  .msg-chat {
    height: calc(100vh - 56px - 24px);
    border-radius: 12px;

    &__feed {
      padding: 12px;
    }
  }
}
</style>
